import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Loader2, Sparkles, ArrowLeft, CheckCircle2, FileText, Wand2, FileDown,
  Lock, Clock, Zap, Rocket, ChevronRight, CreditCard,
} from 'lucide-react';
import { Interview, Markdown } from './JumpstartStudio';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

const FLOWS = [
  { id: 'jumpstart_20', label: 'Free 20-Min Jumpstart', icon: Clock, badge: 'Free', accent: '#0EA5E9',
    desc: 'A quick clarity round. Great to get unstuck — your full AI Blueprint is reserved for a paid session.' },
  { id: 'condensed', label: 'Focused Blueprint (~30 min)', icon: Zap, badge: 'Recommended', accent: '#7C3AED',
    desc: 'A curated interview that produces a solid AI Business Blueprint — mission, offers, audience & launch.' },
  { id: 'deep_dive', label: 'Full Deep Dive', icon: Rocket, badge: 'Complete', accent: '#1D4ED8',
    desc: 'Every question across all 7 stages for the most complete, detailed strategic blueprint.' },
];
const FLOW_META = Object.fromEntries(FLOWS.map((f) => [f.id, f]));

/* ---------- flow chooser ---------- */
function StartCard({ onStart, starting }) {
  const [flow, setFlow] = useState('condensed');
  const [businessName, setBusinessName] = useState('');
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid="client-jumpstart-start">
      <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#F97316] mb-1"><Sparkles size={14} /> Build your vision</div>
      <h3 className="text-lg font-black text-[#0A0F1C]">Start your Business Blueprint</h3>
      <p className="text-sm text-[#64748B] mt-1">Answer on your own time — your strategist sees your progress and helps you refine it.</p>
      <div className="mt-4 space-y-2.5">
        {FLOWS.map((f) => {
          const Icon = f.icon; const on = flow === f.id;
          return (
            <button type="button" key={f.id} onClick={() => setFlow(f.id)} data-testid={`client-flow-${f.id}`}
              className={`w-full text-left rounded-xl border p-3.5 flex items-start gap-3 transition-all ${on ? 'border-[#1D4ED8] bg-[#EFF6FF] shadow-sm' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`}>
              <span className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ background: f.accent }}><Icon size={17} /></span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm text-[#0A0F1C]">{f.label}</span>
                  <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded-full ${f.id === 'jumpstart_20' ? 'bg-sky-100 text-sky-700' : 'bg-emerald-100 text-emerald-700'}`}>{f.badge}</span>
                </div>
                <p className="text-xs text-[#64748B] mt-0.5">{f.desc}</p>
              </div>
              <span className={`shrink-0 w-4 h-4 rounded-full border-2 mt-1 ${on ? 'border-[#1D4ED8] bg-[#1D4ED8]' : 'border-[#CBD5E1]'}`} />
            </button>
          );
        })}
      </div>
      <div className="mt-4">
        <label className="text-xs font-bold uppercase tracking-wide text-[#64748B]">Business / idea name (optional)</label>
        <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} data-testid="client-business-name-input"
          className="w-full mt-1.5 px-3 py-2.5 rounded-lg border border-[#E2E8F0] text-sm focus:border-[#1D4ED8] outline-none" placeholder="e.g. BrightPath Coaching" />
      </div>
      <button type="button" onClick={() => onStart(flow, businessName)} disabled={starting} data-testid="client-start-jumpstart-btn"
        className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#1D4ED8] text-white font-bold text-sm hover:bg-[#1E40AF] disabled:opacity-60">
        {starting ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Start my interview
      </button>
    </div>
  );
}

/* ---------- read-only blueprint for the client ---------- */
function ClientBlueprint({ session }) {
  const md = session.deliverable?.markdown || '';
  const [dl, setDl] = useState(false);
  const copy = () => { navigator.clipboard.writeText(md); toast.success('Blueprint copied'); };
  const downloadPdf = async () => {
    setDl(true);
    try {
      const r = await ax.get(`${API}/portal/coaching/sessions/${session.session_id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${(session.business_name || 'My').replace(/\s+/g, '-')}-Blueprint.pdf`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('Could not generate PDF'); }
    finally { setDl(false); }
  };
  return (
    <div data-testid="client-blueprint-view">
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={copy} data-testid="client-copy-blueprint" className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#475569] hover:border-[#1D4ED8] hover:text-[#1D4ED8]"><FileText size={15} /> Copy</button>
        <button onClick={downloadPdf} disabled={dl} data-testid="client-download-pdf" className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#475569] hover:border-[#1D4ED8] hover:text-[#1D4ED8] disabled:opacity-60">{dl ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} {dl ? 'Building…' : 'PDF'}</button>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5"><Markdown text={md} /></div>
    </div>
  );
}

/* ---------- main client studio ---------- */
export default function ClientJumpstart() {
  const [sessions, setSessions] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [active, setActive] = useState(null);
  const [framework, setFramework] = useState([]);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [gated, setGated] = useState('');
  const [price, setPrice] = useState(49);
  const [checkingOut, setCheckingOut] = useState(false);
  const pollRef = useRef(null);
  const doneRef = useRef(false);
  const [pulling, setPulling] = useState(false);

  const pullVision = async () => {
    if (!active) return;
    setPulling(true);
    const sid = active.session_id;
    try {
      await ax.post(`${API}/portal/coaching/sessions/${sid}/import-website-vision`);
      const started = Date.now();
      const iv = setInterval(async () => {
        const r = await ax.get(`${API}/portal/coaching/sessions/${sid}`).catch(() => null);
        if (!r) return;
        if (r.data.import_status === 'done' || r.data.import_status === 'failed' || Date.now() - started > 240000) {
          clearInterval(iv); setPulling(false);
          if (r.data.import_status === 'done') { setActive(r.data); toast.success(`Filled ${r.data.import_applied || 0} answer(s) from your Website Vision`); }
          else toast.error(r.data.import_error || 'Could not pull from your Website Vision');
        }
      }, 4000);
    } catch (e) { setPulling(false); toast.error(e?.response?.data?.detail || 'Could not pull from your Website Vision'); }
  };

  const load = useCallback(() => {
    ax.get(`${API}/portal/coaching/my-sessions`).then((r) => setSessions(r.data || [])).catch(() => {}).finally(() => setLoadingList(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { ax.get(`${API}/portal/coaching/settings`).then((r) => setPrice(r.data.blueprint_unlock_price || 49)).catch(() => {}); }, []);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const startCheckout = async () => {
    if (!active) return;
    setCheckingOut(true);
    try {
      const { data } = await ax.post(`${API}/payments/checkout`, {
        item_type: 'blueprint_unlock', item_id: active.session_id, origin_url: window.location.origin,
      });
      if (data.url) { window.location.href = data.url; return; }
      toast.error('Could not start checkout');
      setCheckingOut(false);
    } catch (e) {
      setCheckingOut(false);
      toast.error(e?.response?.data?.detail || 'Payment is not available right now');
    }
  };

  const open = async (id) => {
    setGated('');
    try {
      const { data } = await ax.get(`${API}/portal/coaching/sessions/${id}`);
      const fw = await ax.get(`${API}/portal/coaching/framework?flow=${data.flow}`);
      setFramework(fw.data.stages || []);
      setActive(data);
    } catch { toast.error('Could not open your interview'); }
  };

  const start = async (flow, businessName) => {
    setStarting(true);
    try {
      const { data } = await ax.post(`${API}/portal/coaching/sessions`, { flow, business_name: businessName });
      load();
      await open(data.session_id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start'); }
    finally { setStarting(false); }
  };

  const saveAnswer = async (qid, val) => {
    if (!active) return;
    const prev = (active.answers || {})[qid] || '';
    if (prev === val) return;
    setActive((a) => ({ ...a, answers: { ...(a.answers || {}), [qid]: val } }));
    try { await ax.patch(`${API}/portal/coaching/sessions/${active.session_id}`, { answers: { [qid]: val } }); }
    catch { toast.error('Save failed'); }
  };

  const startPoll = (id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    doneRef.current = false;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await ax.get(`${API}/portal/coaching/sessions/${id}`);
        if (doneRef.current) return;
        if (data.status === 'completed' && data.deliverable) {
          doneRef.current = true; clearInterval(pollRef.current); pollRef.current = null;
          setActive(data); setSubmitting(false); load();
          toast.success('Your blueprint is ready!');
        } else if (data.generation_error) {
          doneRef.current = true; clearInterval(pollRef.current); pollRef.current = null;
          setSubmitting(false); toast.error(data.generation_error);
        }
      } catch { /* keep polling */ }
    }, 4000);
  };

  const submit = async () => {
    if (!active) return;
    setSubmitting(true); setGated('');
    try {
      const { data } = await ax.post(`${API}/portal/coaching/sessions/${active.session_id}/submit`);
      if (data.gated) { setSubmitting(false); setGated(data.message); return; }
      if (data.status === 'generating') { startPoll(active.session_id); return; }
      if (data.status === 'submitted') {
        setSubmitting(false);
        setActive((a) => ({ ...a, status: 'submitted' }));
        load();
        toast.success('Submitted! Your strategist will review and build your blueprint.');
      }
    } catch (e) { setSubmitting(false); toast.error(e?.response?.data?.detail || 'Could not submit'); }
  };

  /* ---- detail view ---- */
  if (active) {
    const meta = FLOW_META[active.flow] || {};
    const hasBlueprint = active.status === 'completed' && active.deliverable;
    return (
      <div data-testid="client-jumpstart-detail">
        <button onClick={() => { setActive(null); load(); }} data-testid="client-back-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#475569] hover:text-[#1D4ED8] mb-4"><ArrowLeft size={16} /> Back</button>
        <div className="mb-5">
          <div className="text-xs font-bold uppercase tracking-wide text-[#F97316]">{meta.label || active.flow}</div>
          <h3 className="text-xl font-black text-[#0A0F1C]">{active.business_name || 'My Business Blueprint'}</h3>
        </div>

        {hasBlueprint ? (
          <ClientBlueprint session={active} />
        ) : (
          <>
            {active.status === 'submitted' && (
              <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3" data-testid="client-submitted-banner">
                <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
                <div><div className="font-bold text-emerald-800 text-sm">Submitted for review</div><p className="text-sm text-emerald-700">Your strategist is building your blueprint. You can keep editing your answers if you like.</p></div>
              </div>
            )}
            {gated && (
              <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="client-gated-banner">
                <Lock size={18} className="text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-bold text-amber-900 text-sm">Unlock your full AI Business Blueprint</div>
                  <p className="text-sm text-amber-800">{gated}</p>
                  <button onClick={startCheckout} disabled={checkingOut} data-testid="client-unlock-btn"
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 text-white font-bold text-sm hover:bg-amber-600 disabled:opacity-60">
                    {checkingOut ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />} Unlock full blueprint — ${price}
                  </button>
                </div>
              </div>
            )}
            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-[#BFDBFE] bg-[#EFF6FF] p-3" data-testid="client-pull-vision">
              <span className="flex-1 min-w-[200px] text-sm text-[#1E3A8A]">Already built your Website Vision? Pull in your services, programs, audience and contact info to fill these answers.</span>
              <button onClick={pullVision} disabled={pulling} data-testid="client-pull-vision-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1D4ED8] text-white text-sm font-bold hover:bg-[#1E40AF] disabled:opacity-60">
                {pulling ? <><Loader2 size={15} className="animate-spin" /> Filling answers…</> : <><Wand2 size={15} /> Pull from my Website Vision</>}
              </button>
            </div>
            <Interview session={active} framework={framework} onSaveAnswer={saveAnswer} key={active.updated_at} />
            <div className="sticky bottom-4 mt-6 flex justify-center">
              {active.flow === 'jumpstart_20' && !active.paid_unlock ? (
                <button onClick={startCheckout} disabled={checkingOut} data-testid="client-unlock-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-amber-500 to-[#F97316] text-white font-bold shadow-lg hover:opacity-95 disabled:opacity-70">
                  {checkingOut ? <><Loader2 size={18} className="animate-spin" /> Opening checkout…</> : <><Lock size={18} /> Unlock full Blueprint — ${price}</>}
                </button>
              ) : (
                <button onClick={submit} disabled={submitting} data-testid="client-submit-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#1D4ED8] to-[#F97316] text-white font-bold shadow-lg hover:opacity-95 disabled:opacity-70">
                  {submitting ? <><Loader2 size={18} className="animate-spin" /> Working…</> : <><Wand2 size={18} /> Submit for my Blueprint</>}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  /* ---- list / start view ---- */
  return (
    <div data-testid="client-jumpstart">
      {loadingList ? (
        <div className="flex justify-center py-10"><Loader2 size={24} className="animate-spin text-[#1D4ED8]" /></div>
      ) : sessions.length === 0 ? (
        <StartCard onStart={start} starting={starting} />
      ) : (
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3" data-testid="client-sessions-grid">
            {sessions.filter((s) => s.context !== 'nonprofit').map((s) => (
              <button key={s.session_id} onClick={() => open(s.session_id)} data-testid={`client-session-${s.session_id}`}
                className="text-left bg-white border border-[#E2E8F0] rounded-xl p-4 hover:shadow-md hover:border-[#1D4ED8]/40 transition-all">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-bold text-[#0A0F1C] truncate">{s.business_name || 'My Business Blueprint'}</div>
                  <ChevronRight size={16} className="text-[#CBD5E1] shrink-0" />
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1D4ED8]">{(FLOW_META[s.flow] || {}).label || s.flow}</span>
                  {s.has_deliverable ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600"><CheckCircle2 size={12} /> Blueprint ready</span>
                    : s.status === 'submitted' ? <span className="text-[10px] font-bold text-amber-600">Submitted</span>
                    : <span className="text-[10px] text-[#94A3B8]">In progress</span>}
                </div>
              </button>
            ))}
          </div>
          <StartCard onStart={start} starting={starting} />
        </div>
      )}
    </div>
  );
}
